import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { listToCondition } from "@/shared/domain/query/listGroups";
import type {
  Comparator,
  Condition,
  ConditionGroup,
  SimpleCondition,
} from "@/types";

import type { ConditionOption, FilterDefinition } from "./FilterDefinition";
import {
  acceptsNullChecks,
  identityOperator,
  isDatePreset,
  isListComparator,
  type DatePresetComparator,
  type Filter,
} from "./FilterSchema";
import { identityTypeLikePattern } from "./identityTypes";

dayjs.extend(utc);

/**
 * The condition an `enum` option stands for, when it is a
 * {@link ConditionOption} rather than a plain `field = value`.
 */
export type OptionConditions = (
  field: string,
  value: string,
) => Condition | undefined;

/**
 * The {@link ConditionOption}s among some filter rows' static `enum` options,
 * for {@link FilterToConditionOptions.optionConditions}.
 */
export function optionConditionsOf(
  definitions: readonly {
    type: string;
    field: string;
    options?: unknown;
  }[],
): OptionConditions {
  const conditions = new Map<string, Map<string, Condition>>();
  for (const definition of definitions) {
    if (definition.type !== "enum" || !Array.isArray(definition.options)) {
      continue;
    }
    for (const option of definition.options as unknown[]) {
      if (isConditionOption(option)) {
        const byValue =
          conditions.get(definition.field) ?? new Map<string, Condition>();
        byValue.set(option.value, option.condition);
        conditions.set(definition.field, byValue);
      }
    }
  }
  return (field, value) => conditions.get(field)?.get(value);
}

function isConditionOption(option: unknown): option is ConditionOption {
  return (
    typeof option === "object" &&
    option !== null &&
    "value" in option &&
    "condition" in option
  );
}

export interface FilterToConditionOptions {
  /**
   * Outer wrap operator when there are 2+ filter rows. Defaults to `"AND"`
   * (every filter must match). Pass `"OR"` to join with at-least-one
   * semantics. Single-filter and zero-filter cases ignore this option.
   */
  matchMode?: "AND" | "OR";
  /**
   * The definitions the rows were built against. A list row on a field whose
   * definition allows no null check ({@link acceptsNullChecks}), such as a
   * resource id, is sent without `IS_NULL` / `IS_NOT_NULL`, which the backend
   * would reject there. A row with no definition here is sent with them, so a
   * caller whose rows can be on such a field must pass its definitions.
   */
  definitions?: readonly FilterDefinition[];
  /**
   * The conditions the filtered fields' {@link ConditionOption}s stand for.
   * Without it, every `enum` option is read as `field = value`.
   */
  optionConditions?: OptionConditions;
}

/**
 * Convert the component's `Filter[]` output into a wire-level `Condition`.
 *
 * Empty input → `undefined` (callers omit the `condition` field on
 * `SearchQueryBody` entirely). Multiple filter rows are wrapped in an outer
 * group whose operator is governed by `opts.matchMode` (default `"AND"`).
 * Per-row expansion handles UI-only comparators and multi-value fan-out:
 *
 *   - `ANY_OF` → OR of `EQUALS` per value, plus `IS_NULL` when the filter
 *     includes "Not set"; `NONE_OF` → its complement, AND of `NOT_EQUALS` per
 *     value, plus `IS_NOT_NULL` for "Not set", and otherwise OR `IS_NULL` to
 *     keep the rows with no value (see `listToCondition`, and
 *     `opts.definitions` for the fields that take no null check). A list of
 *     exactly one member is that leaf alone. Blank strings are dropped.
 *   - `EQUALS` / `NOT_EQUALS` → one condition on the filter's single value
 *   - `BETWEEN [a, b]` → AND of `GREATER_THAN_OR_EQUAL a` + `LESS_THAN_OR_EQUAL b`
 *     (exact instants; inclusive on both edges)
 *   - Date relative presets (TODAY / LAST_N_DAYS / THIS_MONTH) → AND of
 *     GTE+LTE bracketing the `now`-relative window, resolved at call time
 *     so a saved filter stays live across days.
 *   - Multi-value with a positive op (CONTAINS/LIKE) → OR group
 *   - Multi-value with a negative op (NOT_CONTAINS/NOT_LIKE) → AND group (set complement)
 *   - IS_NULL / IS_NOT_NULL (enum, boolean) → SimpleCondition with empty
 *     `value` (the wire comparator carries the semantic; `value` is required
 *     by the schema but ignored by the backend for null-checks)
 *
 * The helper assumes valid input — empty values (other than the explicitly
 * empty-allowed IS_NULL / IS_NOT_NULL checks and the valueless date presets)
 * and malformed BETWEEN ranges are rejected upstream by the per-type value
 * inputs' validity checks. Don't call this with arbitrary user-typed text.
 */
export function filterToCondition(
  filters: Filter[],
  opts: FilterToConditionOptions = {},
): Condition | undefined {
  if (filters.length === 0) {
    return undefined;
  }
  const definitions = opts.definitions ?? [];
  if (filters.length === 1) {
    return filterRowToCondition(filters[0], definitions, opts.optionConditions);
  }
  return {
    operator: opts.matchMode ?? "AND",
    conditions: filters.map((filter) =>
      filterRowToCondition(filter, definitions, opts.optionConditions),
    ),
  };
}

/** Whether `filter`'s field takes null checks; see `FilterToConditionOptions.definitions`. */
function takesNullChecks(
  filter: Filter,
  definitions: readonly FilterDefinition[],
): boolean {
  const definition = definitions.find(
    (candidate) =>
      candidate.field === filter.field && candidate.type === filter.type,
  );
  return definition === undefined || acceptsNullChecks(definition);
}

const NEGATIVE_MULTI_VALUE_COMPARATORS = new Set<string>([
  "NOT_CONTAINS",
  "NOT_LIKE",
]);

// Take exactly one value; several values of equality are ANY_OF / NONE_OF.
const SINGLE_VALUE_COMPARATORS = new Set<string>(["EQUALS", "NOT_EQUALS"]);

const NULL_CHECK_COMPARATORS = new Set<string>(["IS_NULL", "IS_NOT_NULL"]);

function filterRowToCondition(
  filter: Filter,
  definitions: readonly FilterDefinition[],
  optionConditions: OptionConditions | undefined,
): Condition {
  const { comparator } = filter;
  // Option-backed filters store labeled options; only the wire `value`
  // crosses this boundary — the display label never reaches a `Condition`.
  const values: readonly (string | number | boolean)[] =
    filter.type === "set" ||
    filter.type === "enum" ||
    filter.type === "identity"
      ? filter.values.map((option) => option.value)
      : filter.values;
  // A metric filter carries the bare dotted path in the UI; the backend's
  // session query convention addresses it as `metric.<name>`, so prepend the
  // prefix at the wire boundary. Everything downstream (single/multi-value
  // fan-out) is identical to numeric.
  const field =
    filter.type === "metric" ? `metric.${filter.field}` : filter.field;

  // `identity` comparators are UI-only names for a kind (IS_USER) or a whole
  // kind (IS_ANY_USER) — neither is a wire comparator, so they never reach the
  // generic path below.
  if (filter.type === "identity") {
    return identityToCondition(field, filter);
  }

  // IS_NULL / IS_NOT_NULL carry their semantic on the comparator; the schema
  // requires a value, but the backend ignores it for null checks.
  if (NULL_CHECK_COMPARATORS.has(comparator)) {
    return { field, comparator: comparator as Comparator, value: "" };
  }

  // The query language has no list comparator, so a list is sent as the
  // group it means (see `listToCondition`). Blank strings are dropped: the
  // schema only asks for one real value, and a blank one would otherwise
  // match the empty string.
  if (isListComparator(filter.comparator)) {
    const list = listToCondition({
      field,
      comparator: filter.comparator,
      values: values.filter(
        (value) => typeof value !== "string" || value.trim().length > 0,
      ),
      includeNull: "includeNull" in filter && filter.includeNull === true,
      nullChecks: takesNullChecks(filter, definitions),
    });
    // An enum list's members are its options, each read as a single value is.
    return filter.type === "enum" && optionConditions !== undefined
      ? withOptionConditions(list, optionConditions)
      : list;
  }

  // UI-only date comparators expand here.
  if (filter.type === "date") {
    if (isDatePreset(filter.comparator)) {
      return datePresetToCondition(field, filter.comparator);
    }
    if (filter.comparator === "BETWEEN") {
      // The datetime input captures exact instants, so both bounds pass
      // through verbatim: inclusive on both edges (GTE lower, LTE upper).
      return {
        operator: "AND",
        conditions: [
          simpleCondition(field, "GREATER_THAN_OR_EQUAL", filter.values[0]),
          simpleCondition(field, "LESS_THAN_OR_EQUAL", filter.values[1]),
        ],
      };
    }
  }

  // An enum option may stand for a whole condition; NOT_EQUALS negates it.
  const valueCondition = (value: boolean | number | string): Condition => {
    const optionCondition =
      filter.type === "enum" && typeof value === "string"
        ? optionConditions?.(field, value)
        : undefined;
    if (optionCondition === undefined) {
      return simpleCondition(field, comparator as Comparator, value);
    }
    return comparator === "NOT_EQUALS"
      ? { operator: "NOT", conditions: [optionCondition] }
      : optionCondition;
  };

  // Single-value path covers every non-multi operator (numeric ranges,
  // boolean EQUALS, single-string EQUALS, etc.). EQUALS / NOT_EQUALS read
  // their one value: the schema rejects more, and several are ANY_OF.
  if (values.length <= 1 || SINGLE_VALUE_COMPARATORS.has(comparator)) {
    return valueCondition(values[0]);
  }

  // Multi-value CONTAINS / LIKE (and their negations): positive ops fan out
  // to OR, negative ops to AND.
  const operator: ConditionGroup["operator"] =
    NEGATIVE_MULTI_VALUE_COMPARATORS.has(comparator) ? "AND" : "OR";
  return {
    operator,
    conditions: values.map(valueCondition),
  };
}

function simpleCondition(
  field: string,
  comparator: Comparator,
  value: boolean | number | string,
): SimpleCondition {
  return { field, comparator, value };
}

/**
 * Expand an `identity` filter, whose operator names a principal kind rather
 * than a wire comparator:
 *
 *   - `IS_ANY_<KIND>` → `LIKE '<kind>:%'`. Valueless, so the kind carried by
 *     the operator is the entire predicate. LIKE rather than BEGINS_WITH
 *     because the backend's `STRING_FIELD_COMPARATORS` allow-list for
 *     `created_by` / `modified_by` permits LIKE; `<kind>:%` is a left-anchored
 *     prefix, equivalent and index-friendly.
 *   - `IS_<KIND>` → `EQUALS` on each selected fully-qualified principal,
 *     fanning a multi-select out to OR (the picked entities are alternatives).
 */
function identityToCondition(
  field: string,
  filter: Extract<Filter, { type: "identity" }>,
): Condition {
  const operator = identityOperator(filter.comparator);
  if (operator === undefined) {
    // Unreachable via the UI: the schema's `identityVariant` only admits
    // comparators that `identityOperator` decodes. Throwing beats emitting a
    // condition on a comparator the backend would reject.
    throw new Error(`Unhandled identity comparator: ${filter.comparator}`);
  }
  if (operator.matchesAnyOfType) {
    return simpleCondition(
      field,
      "LIKE",
      identityTypeLikePattern(operator.type),
    );
  }
  // Only the wire `value` crosses this boundary; the display label the picker
  // captured alongside it never reaches a `Condition`.
  const principals = filter.values.map((option) => option.value);
  if (principals.length <= 1) {
    return simpleCondition(field, "EQUALS", principals[0]);
  }
  return {
    operator: "OR",
    conditions: principals.map((principal) =>
      simpleCondition(field, "EQUALS", principal),
    ),
  };
}

/**
 * Resolve a date preset to a concrete AND-of-GTE-LTE range, anchored to
 * `now`. Lives here (not at filter-creation time inside the value input)
 * so a saved or shared filter named "Last 7 days" still means *the last
 * seven days* the next time the query runs.
 *
 * UTC-anchored to match the date input's own UTC-midnight convention; the
 * upper bound is end-of-day / end-of-month so the window is inclusive on
 * both edges.
 */
function datePresetToCondition(
  field: string,
  comparator: DatePresetComparator,
): ConditionGroup {
  const { start, end } = datePresetRange(comparator);
  return {
    operator: "AND",
    conditions: [
      simpleCondition(field, "GREATER_THAN_OR_EQUAL", start.toISOString()),
      simpleCondition(field, "LESS_THAN_OR_EQUAL", end.toISOString()),
    ],
  };
}

/**
 * The instants a relative date preset spans as of now, both edges inclusive:
 * what {@link filterToCondition} sends for it, and what a list filtered in
 * memory compares against.
 */
export function datePresetRange(comparator: DatePresetComparator): {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
} {
  const now = dayjs.utc();
  const anchor = now.startOf("minute");
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  switch (comparator) {
    case "TODAY":
      start = now.startOf("day");
      end = now.endOf("day");
      break;
    // Hour windows are measured back from now rather than snapped to a day boundary, so they
    // read the same wherever the reader is and do not shrink to a sliver just after UTC
    // midnight the way a day-aligned preset does.
    //
    // The anchor is the current minute, not the current instant. Expansion runs on every
    // render, and an instant-anchored window yields different bounds each time — a new
    // condition, so a new query key, so another fetch, which renders again. Rounding to the
    // minute makes the bounds stable between renders while keeping the window honest to
    // within a minute; the end runs to the close of the current minute so work submitted
    // seconds ago is still inside it.
    case "LAST_3_HOURS":
      start = anchor.subtract(3, "hour");
      end = anchor.add(1, "minute");
      break;
    case "LAST_8_HOURS":
      start = anchor.subtract(8, "hour");
      end = anchor.add(1, "minute");
      break;
    case "LAST_24_HOURS":
      start = anchor.subtract(24, "hour");
      end = anchor.add(1, "minute");
      break;
    case "LAST_7_DAYS":
      start = now.subtract(7, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "LAST_30_DAYS":
      start = now.subtract(30, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "LAST_90_DAYS":
      start = now.subtract(90, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "THIS_MONTH":
      start = now.startOf("month");
      end = now.endOf("month");
      break;
    default: {
      // Exhaustiveness guard — if a new preset lands in `DatePresetComparator`
      // but isn't handled above, this assignment fails to compile.
      const exhaustive: never = comparator;
      throw new Error(`Unhandled date preset: ${String(exhaustive)}`);
    }
  }
  return { start, end };
}

/**
 * `condition` with each `field = value` leaf that an `enum` option stands for
 * (a {@link ConditionOption}) replaced by the option's condition, and each
 * `field != value` leaf by its negation: a list's members read as a single
 * value does.
 */
function withOptionConditions(
  condition: Condition,
  optionConditions: OptionConditions,
): Condition {
  if ("operator" in condition) {
    return {
      ...condition,
      conditions: condition.conditions.map((child) =>
        withOptionConditions(child, optionConditions),
      ),
    };
  }
  const isMember =
    condition.comparator === "EQUALS" || condition.comparator === "NOT_EQUALS";
  const optionCondition =
    isMember && typeof condition.value === "string"
      ? optionConditions(condition.field, condition.value)
      : undefined;
  if (optionCondition === undefined) {
    return condition;
  }
  return condition.comparator === "NOT_EQUALS"
    ? { operator: "NOT", conditions: [optionCondition] }
    : optionCondition;
}
